import React, { useContext, useMemo } from 'react';
import { shallowEqual, useSelector } from 'umi';
import moment from 'moment';
import { FilterContext } from "@/pages/dashboard/components/PageContainer/context";
import Service from '@/pages/dashboard/components/PageContainer/Service';
import { formatFilterValues } from '@/pages/dashboard/utils/utils';
import { getAccumulation, getAccumulationListFromFilterValue } from '@/pages/dashboard/utils/filter';
import { fillRealtimeEventData } from '../../../_config/common';

interface Entry {
  time: string;
  value: string;
}

interface Country {
  name: string;
  list: Entry[];
}

export default function SteamSalesService(props: any) {
  const { style, className, type, config, children, data: propsData, ...resetProps } = props || {};
  const { api, dataKey, isDark = false, refreshKey = '', interval, eventInterval, isDimension, extraParams, bySteamOnly = false, timeZone, } = config || {};
  // const [dataBy, setDataBy] = useState('country'); // 后续下拉组件封装好了需要传入dataBy
  const { filterVal } = useContext(FilterContext);

  const { gameCode, osList } = useSelector((state: any) => ({
    gameCode: state?.dashboard?.currentGame?.game_code,
    osList: state?.dashboardSetting?.selector?.os_list
  }), shallowEqual);

  const steamList = osList?.find((item: { os_name_en: string; }) =>
    item?.os_name_en?.trim()?.toLowerCase() === 'steam'
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const data = useMemo(() => ({
    gameCode,
    isDark,
    ...(propsData || {})
  }), [gameCode, isDark, propsData]);


  return (
    <div className={className} style={style}>
      <Service
        api={api}
        data={{
          ...data,
          extraParams,
        }}
        dataKey={dataKey}
        refreshKey={refreshKey}
        interval={interval}
        beforeRequest={(data) => {
          const { filterVal, gameCode, dataBy, extraParams } = data;

          const accumulationList: string[] = getAccumulationListFromFilterValue(filterVal); // 从筛选器调取accumulation, 例子: by_system, by_region
          const accumulation: Record<string, boolean> = getAccumulation(accumulationList, dataBy); // 通过dataBy找出目前选择的维度

          const { contrastDaily, singleDaily, diff, ...filters } = filterVal || {};
          // 筛选器未更改且外部传入timeZone，则使用外部传入的timeZone偏移的日期timeZomeDate
          const timeZomeDate = moment().utcOffset(Number(timeZone)).format('YYYY-MM-DD');
          const isDefaultTime = diff && timeZone !== undefined && moment(timeZomeDate)?.isValid();

          // 传参兜底筛选器参数
          let params: IApiParamsBase | IApiParamsWithKey = {
            game_code: gameCode,
            filters: {
              ...formatFilterValues(filters),
              ...(bySteamOnly ? { os_list: [steamList?.os_id] } : {})
            },
            date: isDefaultTime ? timeZomeDate : singleDaily,
            contrast_day: contrastDaily,
          };
          // 用户
          if (dataKey === 'date_event_list') {
            delete params.contrast_day;
            delete params.date;
            delete params.filters.diff;
            delete params.filters.timeType;
            params = {
              ...params,
              start_time: singleDaily,
              end_time: singleDaily,
              period: 'hours',
            };
          }
          // 国家/区域、区服
          if (dataKey === 'dimension_sales' || isDimension) {
            params = {
              ...params,
              accumulation,
            };
          }
          if (extraParams) {
            params = {
              ...params,
              ...extraParams,
            };
          }
          return params;
        }}
        trackData={['gameCode', 'isDark', 'hour', 'trend_type', 'is_key_country', 'dataBy', 'extraParams']}
        formatData={(response) => {
          if (dataKey === 'date_event_list') {
            // 事件管理去掉last_moment选项（接口现在不返回，为兼容逻辑在返回值后添加）
            const addItem = {
              date: filterVal?.singleDaily || '',
              time: "last_moment",
              events: 0,
              event_list: []
            };
            const apiData = response?.list?.length ? [...(response?.list || []), addItem] : [];
            return { event: eventInterval ? fillRealtimeEventData(apiData, eventInterval, 143) : apiData };
          }

          if (dataKey === 'dimension_sales' || isDimension) {
            // 占比计算
            const shareDataObj: any = {};
            Object.keys(response).forEach((key) => {
              const shareData: { [time: string]: any; } = {};
              if (key?.slice(-4) === "_top") { // top数据
                // 处理share汇总值
                response?.[key]?.forEach((country: Country) => {
                  country?.list?.forEach((each: Entry) => {
                    const { time, value } = each;
                    if (!shareData?.[time]) {
                      shareData[time] = parseFloat(value) || 0;
                    } else {
                      shareData[time] += parseFloat(value) || 0;
                    }
                  });
                });
                shareDataObj[key?.slice(0, key?.length - 4)] = shareData;
              }
            });
            Object.keys(response).forEach((key) => {
              if (key?.slice(-4) === "_top") { // top数据
                const shareData = shareDataObj?.[key?.slice(0, key?.length - 4)];
                // 处理share值
                response?.[key]?.forEach((val: Country) => {
                  const { list } = val;
                  val.list = list?.map((each) => ({
                    ...each,
                    share: parseFloat(each?.value) / parseFloat(shareData?.[each?.time]) || '-'
                  }));
                });
              }
              if (key?.slice(-5) === "_list") { // list数据
                const shareData = shareDataObj?.[key?.slice(0, key?.length - 5)];
                // 处理share值
                response[key] = response?.[key]?.map((val: Entry) => ({
                  ...val,
                  share: parseFloat(val.value) / parseFloat(shareData?.[val?.time]) || '-'
                }));
              }
            });
          }
          return response;
        }}
      >
        {React.Children.map(children, (child: any) => React.cloneElement(
          child,
          {
            filterVal,
            dataKey,
            ...resetProps,
            state: {}
          }
        ))}
      </Service>
    </div>
  );
};